import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Overline } from "../components/Overline";
import { Button } from "../components/ui/button";

// Renders **bold** segments inside a paragraph
const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        setState("ok");
      })
      .catch(() => setState("missing"));
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[#3d3d3d]" data-testid="blogpost-loading">
        Loading…
      </div>
    );
  }
  if (state === "missing") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blogpost-missing">
        <p className="font-serif text-3xl text-[#333]">Essay not found.</p>
        <Link to="/blog" className="inline-block mt-6 text-[#5fa292]">
          ← Back to journal
        </Link>
      </div>
    );
  }

  return (
    <article data-testid="page-blogpost" className="bg-[#faf9f6]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <Link
          to="/articles"
          data-testid="blogpost-back"
          className="inline-flex items-center gap-2 text-sm text-[#5fa292] hover:text-[#4d8275]"
        >
          <ArrowLeft size={14} /> Back to articles
        </Link>
        <div className="mt-10">
          <Overline>
            {post.category} · {post.read_time}
          </Overline>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] font-normal text-[#333] mt-5">
            {post.title}
          </h1>
          <p className="mt-5 text-sm uppercase tracking-[0.2em] text-[#3d3d3d]">
            By {post.author} · {post.published_at}
          </p>
        </div>

        <div className="mt-12 arch-image aspect-[16/10] bg-[#cbdad6]">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="mt-12 space-y-6 text-lg text-[#333] font-normal leading-relaxed">
          {post.body.split("\n\n").map((p, i) =>
            p.startsWith("**") && p.endsWith("**") ? (
              <h2
                key={i}
                className="font-serif text-2xl md:text-3xl text-[#333] font-normal tracking-tight pt-6"
              >
                {p.slice(2, -2)}
              </h2>
            ) : (
              <p key={i}>{renderInline(p)}</p>
            )
          )}
        </div>

        {post.slug === "personal-health-concierge" && (
          <div
            data-testid="concierge-article-cta"
            className="mt-14 rounded-3xl bg-[#5fa292] text-white p-10 md:p-12 text-center"
          >
            <p className="font-serif text-2xl md:text-3xl tracking-tight leading-snug">
              Six months of personalised, one-to-one support.
              <br />
              <span className="italic">Your health deserves more.</span>
            </p>
            <Link to="/health-concierge" className="inline-block mt-8">
              <Button
                data-testid="concierge-article-cta-button"
                className="bg-white text-[#5fa292] hover:bg-[#faf9f6] rounded-full px-10 h-14 text-base font-semibold"
              >
                I need this
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}

        <div className="mt-16 pt-10 border-t border-[#e5e0d8] text-center">
          <p className="font-serif text-2xl italic text-[#3d3d3d]">
            - Lara
          </p>
        </div>
      </div>
    </article>
  );
}
